import { useId, useState, type Ref } from "react";
import type { Spec } from "../api";
import { CameraButton } from "../camera/CameraButton";
import { config } from "../config";
import { PhotoError, preparePhoto, type PreparedPhoto } from "../image";
import { LEGAL, PHOTO_CONSENT, errorMessage } from "../messages";

interface Props {
  photo: PreparedPhoto | null;
  /** `file`: what was picked (the page keeps it for the other mode) */
  onPhoto: (p: PreparedPhoto | null, file?: File) => void;
  consent: boolean;
  onConsent: (v: boolean) => void;
  consentRef?: Ref<HTMLInputElement>;
  /** the spec chosen in section 2: "Chụp bằng camera" frames its photo and head height */
  cameraSpec?: Spec | null;
}

export function PhotoPicker({ photo, onPhoto, consent, onConsent, consentRef, cameraSpec }: Props) {
  const inputId = useId();
  const consentId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onPhoto(await preparePhoto(file, config.maxUploadSide, config.jpegQuality), file); // the mode frees the old preview
    } catch (e) {
      onPhoto(null);
      setError(errorMessage(e instanceof PhotoError ? e.code : "unsupported_photo"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="picker">
      <label
        htmlFor={inputId}
        className={`dropzone${photo ? " has-photo" : ""}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void choose(e.dataTransfer.files[0]);
        }}
      >
        {photo ? (
          <img src={photo.previewUrl} alt="Ảnh đã chọn" className="preview" />
        ) : (
          <span className="dropzone-text">
            <strong>Chọn hoặc chụp ảnh</strong>
            <small>Chính diện, rõ mặt, đủ sáng. JPG, PNG hoặc WebP.</small>
          </span>
        )}
      </label>
      <input
        id={inputId}
        className="visually-hidden"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        onChange={(e) => void choose(e.target.files?.[0] ?? undefined)}
        disabled={busy}
      />
      <CameraButton spec={cameraSpec} disabled={busy} onPhoto={(file) => void choose(file)} />
      {busy && <p aria-live="polite">Đang chuẩn bị ảnh…</p>}
      {photo && !busy && (
        <p className="hint">
          {photo.width} × {photo.height} px · <label htmlFor={inputId} className="link">đổi ảnh khác</label>
        </p>
      )}
      {error && <p role="alert" className="error">{error}</p>}
      <div className="consent">
        <input ref={consentRef} id={consentId} type="checkbox" checked={consent} onChange={(e) => onConsent(e.target.checked)} />
        <label htmlFor={consentId}>
          {PHOTO_CONSENT} Xem{" "}
          {/* a link inside the label opens the page and does not toggle the box (the link is the activation target) */}
          <a className="link" href={LEGAL.privacy} target="_blank" rel="noopener">Chính sách quyền riêng tư</a>.
        </label>
      </div>
    </div>
  );
}
